import React, { useContext, useEffect, useState } from 'react';
import Page from '../..';
import Table, { TableHeader } from '../../../Table';
import { setFilterColumn } from '../../../../store/utils';
import Form from '../../../Form';
import SVG from '../../../SVG';
import { ExportContext } from '../../../../provider/ExportProvider';
import { formatBRL, formatCompleteBRL } from '../../../../store/masks';
import { useAbility } from '../../../Hooks/useAbility';

export default function PageExportsInvoices() {
  const {
    invoices,
    checkInvoiceIds,
    allInvoiceChecks,
    invoiceCheckAll,
    checkInvoiceId,
    editInvoice,
    duplicateInvoice,
    searchFilter,
    setSearchFilter,
    getFilterField,
    getSortField,
    setSortField,
    getAutocompleteItems,
    setAutocompleteItems,
    setField,
    onLineClickHandler,
    setInvoice,
    TableControl,
    reportInvoice,
    situations,
    labels,
    setModal,
    filterGroupInvoice,
    user,
    selectedGroupId,
  } = useContext(ExportContext)!.controllers.invoice;
  const { Can, ability } = useAbility();
  const canDeleteInvoice = ability?.can('delete', 'Invoice');
  const canUpdateInvoice = ability?.can('update', 'Invoice');
  const canCreateInvoice = ability?.can('create', 'Invoice');

  const canUpdateOrDeleteInvoice = canDeleteInvoice || canUpdateInvoice;
  const canCreateOrUpdateOrDeleteInvoice =
    canCreateInvoice || canUpdateOrDeleteInvoice;

  const canUpdateAndDeleteInvoice = canDeleteInvoice && canUpdateInvoice;
  const canCreateAndUpdateAndDeleteInvoice =
    canCreateInvoice && canUpdateAndDeleteInvoice;

  const situationFilter = getFilterField('status');
  const labeledFilter = getFilterField('labeled');

  const taxNames: any[] = [
    ...new Set(
      invoices.flatMap(({ invoiceTaxes }) =>
        invoiceTaxes.map(({ tax: { name } }) => name),
      ),
    ),
  ];

  const items = invoices.map((invoice, index) => ({
    $isSelected: checkInvoiceIds.includes(invoice.id),
    check: () => (
      <Form.Input.Checkbox
        checked={checkInvoiceIds.includes(invoice.id)}
        onChange={() => checkInvoiceId(invoice.id)}
      />
    ),
    ...invoice,
    ...(() =>
      canCreateOrUpdateOrDeleteInvoice
        ? {
          control: () => (
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                columnGap: '10px',
              }}
            >
              <Can I="create" a="Invoice">
                {() => (
                  <label
                    style={{
                      display: 'flex',
                      cursor: 'pointer',
                      alignItems: 'center',
                    }}
                  >
                    {true ? (
                      <SVG.Icon.Duplicate
                        onClick={() => {
                          duplicateInvoice(invoice);
                        }}
                        width={25}
                        height={25}
                      />
                    ) : (
                      <SVG.Icon.Loader width={25} height={25} />
                    )}
                  </label>
                )}
              </Can>

              <Can I="update" a="Invoice">
                {() => (
                  <label
                    style={{
                      display: 'flex',
                      cursor: 'pointer',
                      alignItems: 'center',
                    }}
                  >
                    {true ? (
                      <SVG.Icon.Edit
                        onClick={() => {
                          editInvoice(invoice);
                        }}
                        width={25}
                        height={25}
                      />
                    ) : (
                      <SVG.Icon.Loader width={25} height={25} />
                    )}
                  </label>
                )}
              </Can>
            </label>
          ),
        }
        : {})(),
  }));

  const headers: TableHeader[] = [
    {
      text: (
        <Form.Input.Checkbox
          checked={allInvoiceChecks}
          onChange={invoiceCheckAll}
        />
      ),
      value: 'check',
      width: 'auto',
      isFixed: true,
      lock: 'locked',
    },
    {
      text: 'Produto',
      value: 'product.resume',
      mask: ({ product: { resume, code } }) => `${code} - ${resume}`,
      width: '250px',
      isFixed: true,
      lock: 'locked',
      sort: {
        value: getSortField('product'),
        onChange: setSortField('product'),
      },
      filter: {
        value: getFilterField('product'),
        onChange: setField('product'),
        items: getAutocompleteItems('product'),
        handleItems: () => setAutocompleteItems('product'),
        label: 'Produto',
      },
      onClear: () => {
        setSearchFilter(setFilterColumn('product', '', searchFilter));
        setInvoice(`autocompletes.invoices.product.filter.value`, '');
      },
    },
    {
      text: 'Companhia',
      value: 'company.name',
      width: '170px',
      lock: 'locked',
      sort: {
        value: getSortField('company'),
        onChange: setSortField('company'),
      },
      filter: {
        value: getFilterField('company'),
        onChange: setField('company'),
        items: getAutocompleteItems('company'),
        handleItems: () => setAutocompleteItems('company'),
        label: 'Companhia',
      },
      onClear: () => {
        setSearchFilter(setFilterColumn('company', '', searchFilter));
        setInvoice(`autocompletes.invoices.company.filter.value`, '');
      },
    },
    {
      text: 'Data',
      value: 'date',
      width: '120px',
      lock: 'locked',
      mask: ({ date }) => new Date(date).toLocaleString('pt-BR').split(',')[0],
      sort: {
        value: getSortField('date'),
        onChange: setSortField('date'),
      },
      interval: [
        {
          value: getFilterField('date', 'GEQ'),
          onChange: setField('date', 'GEQ'),
          label: 'Data inicial',
        },
        {
          value: getFilterField('date', 'LT'),
          onChange: setField('date', 'LT'),
          label: 'Data final',
        },
      ],
      tooltip: {
        translateX: 0.5,
      },
      onClear: () => {
        let filter = setFilterColumn('date', '', searchFilter, 'GEQ');
        filter = setFilterColumn('date', '', filter, 'LT');
        setSearchFilter(filter);
      },
    },
    {
      text: 'CRT',
      value: 'crt',
      width: '130px',
      lock: 'locked',
      sort: {
        value: getSortField('crt'),
        onChange: setSortField('crt'),
      },
      filter: {
        value: getFilterField('crt'),
        onChange: setField('crt'),
        items: getAutocompleteItems('crt'),
        handleItems: () => setAutocompleteItems('crt'),
        label: 'CRT',
      },
      onClear: () => {
        setSearchFilter(setFilterColumn('crt', '', searchFilter));
        setInvoice(`autocompletes.invoices.crt.filter.value`, '');
      },
      style: {
        fontWeight: 'bold',
      },
    },
    {
      text: 'Nota',
      value: 'invoiceNumber',
      width: '110px',
      lock: 'locked',
      sort: {
        value: getSortField('invoiceNumber'),
        onChange: setSortField('invoiceNumber'),
      },
      filter: {
        value: getFilterField('invoiceNumber'),
        onChange: setField('invoiceNumber'),
        items: getAutocompleteItems('invoiceNumber'),
        handleItems: () => setAutocompleteItems('invoiceNumber'),
        label: 'Nota',
      },
      onClear: () => {
        setSearchFilter(setFilterColumn('invoiceNumber', '', searchFilter));
        setInvoice(`autocompletes.invoices.invoiceNumber.filter.value`, '');
      },
    },
    {
      text: 'Valor NF',
      value: 'invoiceValue',
      width: '130px',
      mask: ({ invoiceValue }) => formatBRL(invoiceValue),
      lock: 'locked',
      sort: {
        value: getSortField('invoiceValue'),
        onChange: setSortField('invoiceValue'),
      },
      total: reportInvoice.totalInvoiceValue,
      totalMask: formatBRL,
    },
    {
      text: 'Qtd NF',
      value: 'invoiceQuantity',
      width: '100px',
      lock: 'locked',
      sort: {
        value: getSortField('invoiceQuantity'),
        onChange: setSortField('invoiceQuantity'),
      },
    },
    {
      text: 'Custo unit real',
      value: 'unitCostReal',
      width: '150px',
      lock: 'locked',
      mask: ({ unitCostReal }) => formatBRL(unitCostReal),
      sort: {
        value: getSortField('unitCostReal'),
        onChange: setSortField('unitCostReal'),
      },
    },
    {
      text: 'Valor real',
      value: 'realValue',
      width: '130px',
      lock: 'locked',
      mask: ({ realValue }) => formatBRL(realValue),
      sort: {
        value: getSortField('realValue'),
        onChange: setSortField('realValue'),
      },
      total: reportInvoice.totalRealValue,
      totalMask: formatBRL,
    },
    {
      text: 'Situação',
      value: 'status',
      width: '110px',
      lock: 'locked',
      mask: ({ status }) =>
        situations.find(({ data }) => data === status)?.name ?? '',
      filter: {
        label: 'Situação',
        value:
          situations.find(({ data }) => data === situationFilter)?.name ?? '',
        items: situations,
        onChange: () => { },
        onSelected: ({ data }) => {
          let filter = setFilterColumn('status', data, searchFilter);
          setSearchFilter({ ...filter, page: 1 });
        },
      },
      tooltip: {
        translateX: 0.5,
      },
      onClear: () => {
        let filter = setFilterColumn('status', '', searchFilter);
        setSearchFilter(filter);
      },
      color: () => 'white',
      background: ({ status }) =>
        status === 'RECEIVED' ? '#02a34b' : '#FF0050',
      style: {
        justifyContent: 'center',
        borderRadius: '3px',
        fontWeight: 'bold',
      },
    },
    {
      text: 'Etiquetado',
      value: 'labeled',
      width: '120px',
      lock: 'locked',
      mask: ({ labeled }) =>
        labels.find(({ data }) => data === labeled)?.name ?? '',
      filter: {
        label: 'Etiquetado',
        value: labels.find(({ data }) => data === labeledFilter)?.name ?? '',
        items: labels,
        onChange: () => { },
        onSelected: ({ data }) => {
          let filter = setFilterColumn('labeled', data, searchFilter, 'EQUAL');
          setSearchFilter({ ...filter, page: 1 });
        },
      },
      tooltip: {
        translateX: 0.5,
      },
      onClear: () => {
        let filter = setFilterColumn('labeled', '', searchFilter);
        setSearchFilter(filter);
      },
      style: {
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '3px',
        fontWeight: 'bold',
      },
    },
    {
      text: 'Custo total',
      value: 'totalCost',
      width: '130px',
      lock: 'locked',
      mask: ({ totalCost }) => formatBRL(totalCost),
      sort: {
        value: getSortField('totalCost'),
        onChange: setSortField('totalCost'),
      },
      total: reportInvoice.totalTotalCost,
      totalMask: formatBRL,
    },
    {
      text: 'Custo unit total',
      value: 'unitCostTotal',
      width: '130px',
      lock: 'locked',
      mask: ({ unitCostTotal }) => formatBRL(unitCostTotal),
      sort: {
        value: getSortField('unitCostTotal'),
        onChange: setSortField('unitCostTotal'),
      },
    },
    {
      text: 'Venda unit',
      value: 'unitSale',
      width: '130px',
      lock: 'locked',
      mask: ({ unitSale }) => formatBRL(unitSale),
      sort: {
        value: getSortField('unitSale'),
        onChange: setSortField('unitSale'),
      },
    },
    {
      text: 'Lucro unit',
      value: 'unitProfit',
      width: '130px',
      lock: 'locked',
      mask: ({ unitProfit }) => formatCompleteBRL(unitProfit),
      color: ({ unitProfit }) => (unitProfit < 0 ? '#ff0050' : 'black'),
      sort: {
        value: getSortField('unitProfit'),
        onChange: setSortField('unitProfit'),
      },
    },
    {
      text: 'Lucro total',
      value: 'totalProfit',
      width: '130px',
      lock: 'locked',
      mask: ({ totalProfit }) => formatCompleteBRL(totalProfit),
      color: ({ totalProfit }) => (totalProfit < 0 ? '#ff0050' : 'black'),
      sort: {
        value: getSortField('totalProfit'),
        onChange: setSortField('totalProfit'),
      },
      total: reportInvoice.totalTotalProfit,
      totalMask: formatCompleteBRL,
    },
    ...taxNames.map((name) => ({
      text: name,
      value: '',
      style: { color: '#001c41' },
      color: () => '#001c41',
      mask: ({ invoiceTaxes }: Invoice) => {
        const invoiceTax = invoiceTaxes.find(
          ({ tax: { name: n } }) => n === name,
        );
        if (!invoiceTax) return formatBRL(0);
        return formatBRL(invoiceTax.value);
      },
      width: '120px',
      lock: 'locked',
      total: reportInvoice.totalTaxes.find(({ tax }) => tax.name === name)
        ?.total,
      totalMask: formatCompleteBRL,
    })),
    {
      text: 'Criado em',
      value: 'createdAt',
      width: '160px',
      lock: 'locked',
      mask: ({ createdAt }) => new Date(createdAt).toLocaleString('pt-BR'),
      sort: {
        value: getSortField('createdAt'),
        onChange: setSortField('createdAt'),
      },
      interval: [
        {
          value: getFilterField('createdAt', 'GEQ'),
          onChange: setField('createdAt', 'GEQ'),
          label: 'Data inicial',
        },
        {
          value: getFilterField('createdAt', 'LT'),
          onChange: setField('createdAt', 'LT'),
          label: 'Data final',
        },
      ],
      tooltip: {
        translateX: 0.5,
      },
      onClear: () => {
        let filter = setFilterColumn('createdAt', '', searchFilter, 'GEQ');
        filter = setFilterColumn('createdAt', '', filter, 'LT');
        setSearchFilter(filter);
      },
    },
    {
      text: 'Atualizado em',
      value: 'updatedAt',
      width: '180px',
      lock: 'locked',
      mask: ({ updatedAt }) => new Date(updatedAt).toLocaleString('pt-BR'),
      sort: {
        value: getSortField('updatedAt'),
        onChange: setSortField('updatedAt'),
      },
      tooltip: {
        translateX: 1,
      },
      interval: [
        {
          value: getFilterField('updatedAt', 'GEQ'),
          onChange: setField('updatedAt', 'GEQ'),
          label: 'Data inicial',
        },
        {
          value: getFilterField('updatedAt', 'LT'),
          onChange: setField('updatedAt', 'LT'),
          label: 'Data final',
        },
      ],
      onClear: () => {
        let filter = setFilterColumn('updatedAt', '', searchFilter, 'GEQ');
        filter = setFilterColumn('updatedAt', '', filter, 'LT');
        setSearchFilter(filter);
      },
    },
    ...(() =>
      canCreateOrUpdateOrDeleteInvoice
        ? [
          {
            text: '',
            value: 'control',
            lock: 'locked',
            width: canUpdateAndDeleteInvoice ? '80px' : '40px',
            isFixed: true,
            fixedPosition: 'right' as any,
          },
        ]
        : [])(),
  ];

  useEffect(() => {
    if (!selectedGroupId)
      filterGroupInvoice()
  }, [user, selectedGroupId])

  return (
    <Page className="page-banner">
      <div
        style={{
          position: 'relative',
          transition: 'min-width 500ms, max-width 500ms',
          width: '100%',
          minHeight: 'calc(100vh - 130px)',
          maxHeight: 'calc(100vh - 130px)',
          margin: '0 1rem',
          background: '#DDDDDD',
          borderRadius: '5px',
          overflow: 'hidden',
        }}
      >
        <Table
          headers={headers}
          items={items}
          size="small"
          onLineClick={onLineClickHandler}
          containerStyle={{ height: 'calc(100vh - 210px)' }}
        >
          <TableControl />
        </Table>
      </div>
    </Page>
  );
}
